import { useState } from 'react';
import { Link } from 'react-router-dom';
import { restaurants, categories } from '../../mocks/foodDrink';
import { useSEO } from '../../utils/seo';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function FoodDrinkPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  useSEO({
    title: 'Best Restaurants in Roseville CA - Dining Guide, Brunch, Coffee Shops & Bars',
    description: 'Discover the best restaurants in Roseville California. Find top-rated brunch spots, coffee shops, Italian restaurants, Mexican food, sushi, pizza, and bars. Local dining guide with reviews and menus.',
    keywords: 'best restaurants in Roseville CA, brunch Roseville CA, coffee shops Roseville, Italian restaurants Roseville, Mexican food Roseville, sushi Roseville, pizza Roseville, bars Roseville California',
    canonical: '/food-drink',
    ogImage: 'https://public.readdy.ai/ai/img_res/906b73bebd35f0f3cccd0155c763695c.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Restaurants & Dining Guide',
          description: 'Complete guide to the best restaurants, cafes, and bars in Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/food-drink`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Food & Drink', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/food-drink` },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What are the best restaurants in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville has over 300 restaurants ranging from fine dining to casual eateries. Top picks include farm-to-fork California cuisine, authentic Mexican food, craft breweries, and award-winning brunch spots throughout the city.',
              },
            },
            {
              '@type': 'Question',
              name: 'Where are the best brunch spots in Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville offers excellent brunch options including spots near the Westfield Galleria, Downtown Old Town, and along Douglas Boulevard. Many feature outdoor patios, bottomless mimosas, and locally sourced ingredients.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are there craft breweries in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! Roseville has 15+ craft breweries and taprooms. Many offer food menus, live music, and family-friendly environments. The local craft beer scene is one of the most vibrant in Placer County.',
              },
            },
          ],
        },
      ],
    },
  });

  const filteredRestaurants = selectedCategory === 'all'
    ? restaurants
    : restaurants.filter(restaurant => {
        if (selectedCategory === 'fine-dining') return restaurant.category === 'Fine Dining';
        if (selectedCategory === 'casual') return restaurant.category === 'Casual Dining';
        if (selectedCategory === 'breweries') return restaurant.category === 'Brewery & Restaurant';
        return false;
      });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 cursor-pointer">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
              <Link to="/recreation" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Recreation</Link>
              <Link to="/real-estate" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Real Estate</Link>
              <Link to="/utilities" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Utilities</Link>
              <Link to="/food-drink" className="text-teal-600 font-semibold cursor-pointer">Food & Drink</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-orange-500 to-red-600">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl font-bold mb-4">Food & Drink Guide</h1>
          <p className="text-xl text-orange-100 max-w-2xl mx-auto">
            From farm-to-fork fine dining to casual neighborhood favorites, explore Roseville's diverse culinary scene
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full font-semibold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={category.icon}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurants Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredRestaurants.map((restaurant) => (
              <Link
                key={restaurant.id}
                to={`/food-drink/${restaurant.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={restaurant.image}
                    alt={restaurant.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      {restaurant.cuisine}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                      <span className="text-sm font-semibold text-gray-900">{restaurant.rating}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                      {restaurant.name}
                    </h3>
                    <span className="text-teal-600 font-semibold text-lg">{restaurant.priceRange}</span>
                  </div>

                  <p className="text-sm text-gray-500 mb-3">{restaurant.category}</p>

                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {restaurant.description}
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-map-pin-line text-teal-500"></i>
                      <span>{restaurant.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-time-line text-teal-500"></i>
                      <span>{restaurant.hours}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-teal-600 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      View Details
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-restaurant-2-line text-teal-600 text-2xl"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">300+</div>
              <div className="text-sm text-gray-600">Restaurants</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-beer-line text-teal-600 text-2xl"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">15+</div>
              <div className="text-sm text-gray-600">Craft Breweries</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-cup-line text-teal-600 text-2xl"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">50+</div>
              <div className="text-sm text-gray-600">Cafes & Coffee</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-white rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-goblet-line text-teal-600 text-2xl"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">25+</div>
              <div className="text-sm text-gray-600">Wine & Cocktail Bars</div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-start gap-3 justify-center">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-teal-500 text-sm"></i>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Data provided by Roseville Area Chamber of Commerce, Yelp, and participating local restaurants and establishments
                </p>
                <p className="text-[11px] text-gray-400 mt-1 flex items-center justify-center gap-1">
                  <i className="ri-time-line"></i>Data last updated: March 18, 2025
                  <span className="mx-2">•</span>
                  <a href="/data-sources" className="text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer">
                    View All Sources <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MailchimpNewsletter />

      {/* Footer */}
      <footer className="bg-teal-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </div>
            <p className="text-gray-600 text-sm">
              © 2025 Roseville.org. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="text-teal-600 hover:underline cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
